<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';

// Propuesta 2 · 2026-05-19 (Victor): control segmentado monocromo M1.B con
// los filtros más usados fijos arriba (Todos · Mi Chat · Urgentes · Tareas) +
// "Más filtros ▾" desplegable para el resto. 1 clic, sin tantos colores.
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: wootConstants.ASSIGNEE_TYPE.ME,
  },
});

const emit = defineEmits(['chatTabChange']);

// Filtros prioritarios fijos (en orden) · etiqueta corta y limpia.
// Selección / Retoque (Victor 2026-06-11): fijos al lado de Todos · solo
// aparecen si vienen en items (rol Selección/Retoque), con su punto de color.
// Orden (Victor 2026-06-11): Todos · Mi Chat · Urgentes · Clientes · Selección ·
// Retoque · Impresión · Álbum · Cerrados. Cada uno solo aparece si viene en items
// (los de departamento se gatean por rol). Tareas pasa al menú "Más".
const PRIORITY = [
  { key: wootConstants.ASSIGNEE_TYPE.ALL, label: 'Todos' },
  { key: wootConstants.ASSIGNEE_TYPE.ME, label: 'Mi Chat' },
  { key: 'urgent', label: 'Urgentes' },
  { key: 'clientes', label: 'Clientes' },
  { key: 'seleccion', label: 'Selección' },
  { key: 'retoque', label: 'Retoque' },
  { key: 'impresion', label: 'Impresión' },
  { key: 'album', label: 'Álbum' },
  { key: 'closed', label: 'Cerrados' },
];

const quickItems = computed(() =>
  PRIORITY.map(p => {
    const found = props.items.find(i => i.key === p.key);
    return found ? { ...found, label: p.label } : null;
  }).filter(Boolean)
);

const moreItems = computed(() =>
  props.items.filter(i => !PRIORITY.some(p => p.key === i.key))
);

const activeInMore = computed(() =>
  moreItems.value.find(i => i.key === props.activeTab)
);

const open = ref(false);
function toggle() {
  open.value = !open.value;
}
function close() {
  open.value = false;
}

function select(key) {
  if (key !== props.activeTab) emit('chatTabChange', key);
  close();
}

const activeTabIndex = computed(() =>
  props.items.findIndex(item => item.key === props.activeTab)
);
const keyboardEvents = {
  'Alt+KeyN': {
    action: () => {
      const nextIndex = (activeTabIndex.value + 1) % props.items.length;
      const next = props.items[nextIndex];
      if (next) select(next.key);
    },
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div v-on-click-outside="close" class="relative w-full px-3 py-1.5">
    <div
      class="flex items-center gap-[2px] p-[3px] rounded-lg bg-n-alpha-1 border border-n-weak"
    >
      <button
        v-for="q in quickItems"
        :key="q.key"
        type="button"
        class="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] whitespace-nowrap transition-colors"
        :class="q.key === activeTab
          ? 'bg-[rgba(212,175,106,0.14)] text-[#f0e2c4] font-semibold'
          : 'text-n-slate-11 hover:text-n-slate-12'"
        @click="select(q.key)"
      >
        <span
          v-if="q.dot"
          class="size-[7px] rounded-full flex-shrink-0"
          :style="{ backgroundColor: q.dot }"
        />
        <span class="truncate">{{ q.label }}</span>
        <span
          v-if="q.count !== undefined && q.count !== null"
          class="text-[9.5px] font-bold"
          :class="q.key === activeTab ? 'text-[#d4af6a]' : 'text-n-slate-10'"
        >{{ q.count.toLocaleString('es-DO') }}</span>
      </button>

      <button
        v-if="moreItems.length"
        type="button"
        class="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] whitespace-nowrap transition-colors"
        :class="activeInMore
          ? 'bg-[rgba(212,175,106,0.14)] text-[#f0e2c4] font-semibold'
          : 'text-n-slate-11 hover:text-n-slate-12'"
        @click="toggle"
      >
        <span class="truncate max-w-[88px]">{{
          activeInMore ? activeInMore.name : 'Más'
        }}</span>
        <span
          class="i-lucide-chevron-down size-3.5 transition-transform"
          :class="open ? 'rotate-180' : ''"
        />
      </button>
    </div>

    <div
      v-if="open && moreItems.length"
      class="absolute left-3 right-3 top-full mt-1.5 z-50 bg-n-background border border-n-weak rounded-xl shadow-2xl overflow-hidden py-1"
    >
      <button
        v-for="item in moreItems"
        :key="item.key"
        type="button"
        class="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left transition-colors"
        :class="item.key === activeTab
          ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
          : 'text-n-slate-11 hover:bg-n-alpha-1'"
        @click="select(item.key)"
      >
        <span class="truncate flex-1">{{ item.name }}</span>
        <span
          v-if="item.count !== undefined && item.count !== null"
          class="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11"
        >{{ item.count }}</span>
      </button>
    </div>
  </div>
</template>
